import { useMediaState } from "@tezzro/player";
import { useCallback, useMemo } from "react";
import { CameraCalibration } from "@/shared/api";
import {
  useCalibrationSchedulesQuery,
  useCalibrationsQuery,
} from "@/shared/api/insights-api";

export function useCalibrationData() {
  const isLive = useMediaState("isLive");
  const toTime = useMediaState("toTime");

  const calibrationSchedulesQuery = useCalibrationSchedulesQuery(
    isLive
      ? { from_datetime: undefined, to_datetime: undefined }
      : {
          from_datetime: undefined,
          to_datetime: toTime ? new Date(toTime).toISOString() : undefined,
        }
  );

  const currentCalibrationId =
    calibrationSchedulesQuery.data?.results.at(-1)?.id || 0;

  const calibrationsQuery = useCalibrationsQuery(
    { calibrationId: currentCalibrationId },
    { skip: !calibrationSchedulesQuery.data }
  );

  const findCameraCalibration = useCallback(
    (
      targetCameraId: string | undefined
    ): CameraCalibration | null | undefined => {
      if (!calibrationsQuery.data || !targetCameraId) return null;
      return calibrationsQuery.data.result.camera_calibrations.find(
        ({ camera_id }) => camera_id === targetCameraId
      );
    },
    [calibrationsQuery.data]
  );

  const calibrationOptions = useMemo(() => {
    return calibrationsQuery.data?.result.camera_calibrations
      .map(({ camera_id }) => ({ label: camera_id, value: camera_id }))
      .sort((a, b) => Number(a.value) - Number(b.value));
  }, [calibrationsQuery.data]);

  return {
    calibrationOptions,
    calibrations: calibrationsQuery.data,
    currentCalibrationId,
    isCalibrationSchedulesLoading: calibrationSchedulesQuery.isLoading,
    isCalibrationLoading: calibrationsQuery.isLoading,
    findCameraCalibration,
  };
}
